import React, { useEffect, useState } from 'react';
import { nanoid } from 'nanoid';
import styles from './page.module.css'
import '@/index.css';
import { GRAPH_API_VERSION } from '@tera/shared/src/lib/graphApi';
import { configIdParaModo, parseSignupMode } from '@/lib/whatsappSignup';
import { ehEventoConclusao, SIGNUP_FINISH_COEXISTENCIA } from '@/lib/whatsappSignupEventos';

// Definição do tipo FB para o TypeScript
declare global {
    interface Window {
        FB: any;
        csrfToken: string;
    }
}

// Status definidos para controlar os estados de UI
type AuthStatus =
    'loading' |
    'ready' |
    'authenticating' |
    'success' |
    'error' |
    'blocked' |
    'invalid-origin';

type PageProps = {};

const AuthFbPage = ({ }: PageProps) => {
    // Estado para gerenciar a interface do usuário
    const [status, setStatus] = useState<AuthStatus>('loading');
    const [message, setMessage] = useState('Carregando...');
    const [remainingTime, setRemainingTime] = useState(0);
    const [isSdkLoaded, setSdkLoaded] = useState(false);

    // Ref para armazenar os dados de signup do WhatsApp
    const signupData = React.useRef<any>(null);

    // Preenchida pelo evento de conclusão do Embedded Signup (ver
    // `ehEventoConclusao`). Ref, e não state, porque é lida dentro do callback
    // do FB.login, que captura o closure do render em que foi criado.
    const coexistencia = React.useRef(false);

    // Extrair token CSRF da URL ou gerar um novo como fallback
    const [csrfToken] = useState(() => {
        // Tentar obter o token da URL
        const urlParams = new URLSearchParams(window.location.search);
        const tokenFromUrl = urlParams.get('csrf');

        if (tokenFromUrl) {
            console.log('Token CSRF recebido da URL:', tokenFromUrl);
            return tokenFromUrl;
        } else {
            console.log('Token CSRF não encontrado na URL. Gerando novo token.');
            return nanoid(); // Fallback para caso não haja token na URL
        }
    });

    // Modo do signup: 'coex' usa a configuração de coexistência (número já em uso
    // no app WhatsApp Business), que é um config_id diferente no Meta App Dashboard.
    const [signupMode] = useState(() =>
        parseSignupMode(new URLSearchParams(window.location.search).get('mode'))
    );

    // Extrair a origin do opener da URL para evitar acessar window.opener.location.origin (causa SecurityError cross-origin)
    const [openerOrigin] = useState(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const originFromUrl = urlParams.get('origin');
        if (originFromUrl) {
            console.log('Origin do opener recebida da URL:', originFromUrl);
            return originFromUrl;
        }
        // Fallback: usar a própria origin (funciona quando opener é same-origin)
        return window.location.origin;
    });

    // Outros estados para rastreamento de segurança
    const [attempts, setAttempts] = useState(0);
    const [isBlocked, setIsBlocked] = useState(false);
    const [lastAttemptTime, setLastAttemptTime] = useState<number>(0);

    // Constantes de segurança
    const MAX_ATTEMPTS = 5;
    const BLOCK_TIMEOUT = 60000; // 1 minuto de bloqueio após exceder tentativas

    // Sanitiza HTML para evitar XSS
    const sanitizeHTML = (text: string): string => {
        const element = document.createElement('div');
        element.textContent = text;
        return element.innerHTML;
    };

    // Função para enviar mensagem de erro para a página pai
    const sendErrorToParent = (errorMessage: string) => {
        const sanitizedError = sanitizeHTML(errorMessage);
        setMessage(sanitizedError);
        setStatus('error');

        if (window.opener) {
            try {
                console.log('Enviando erro - Token CSRF usado:', csrfToken);
                window.opener.postMessage({
                    type: "facebook_auth",
                    error: sanitizedError,
                    csrfToken: csrfToken // Usar o token do estado React diretamente
                }, openerOrigin);
            } catch (e) {
                console.error("Erro ao enviar mensagem para janela pai:", e);
            }
        }
    };    // Função para enviar o código de autenticação para a página pai
    const sendAuthCodeToParent = (code: string | null, signupData: any) => {
        if (!window.opener) {
            setStatus('error');
            setMessage('Esta página deve ser aberta como um popup do TeraChat.');
            return;
        }

        try {
            // Garantir que estamos usando o token CSRF correto
            console.log('Token CSRF usado para enviar mensagem:', window.csrfToken);
            console.log('Token CSRF do estado React:', csrfToken);
            console.log('Enviando dados para janela principal:', { code, signupData });

            // Incluir o token CSRF na mensagem para validação na página pai
            window.opener.postMessage({
                type: "facebook_auth",
                code: code,
                signupData: signupData, // Enviar os dados exatamente como recebidos
                coexistence: coexistencia.current, // Define se o backend pula o /register
                csrfToken: csrfToken // Usar o token do estado React diretamente
            }, openerOrigin);

            // Limpar dados sensíveis
            setTimeout(() => {
                window.csrfToken = '';
            }, 100);

            // Atualizar estado para sucesso
            setStatus('success');
            setMessage('Você foi autenticado com sucesso. Esta janela será fechada automaticamente.');

            // Fechar a janela após um breve atraso
            setTimeout(() => {
                window.close();
            }, 2000);
        } catch (error) {
            sendErrorToParent(`Erro ao processar autenticação: ${sanitizeHTML(String(error))}`);
        }
    };

    // Função para verificar os dados de inscrição do WhatsApp
    const checkWhatsAppSignupData = (response: any) => {
        console.log("Verificando dados de inscrição do WhatsApp:", response);

        // Primeiro verificar se já temos os dados via postMessage
        if (signupData.current) {
            console.log("Usando dados de signup armazenados via postMessage:", signupData.current);
            sendAuthCodeToParent(response.authResponse?.code || null, signupData.current);
            return;
        }

        // Para manipular dados do WhatsApp Embedded Signup, se houver
        if (response.signupData) {
            console.log("Usando signupData da resposta:", response.signupData);
            // Enviar os dados completos do signup
            sendAuthCodeToParent(response.authResponse?.code || null, response.signupData);
        } else if (response.authResponse) {
            console.log("Usando authResponse como dados:", response.authResponse);
            // Se não tiver signupData mas tiver authResponse, enviar os dados da authResponse
            // pois eles podem conter informações necessárias
            sendAuthCodeToParent(response.authResponse.code || null, response.authResponse);
        } else {
            console.error("Nenhum dado de autenticação encontrado");
            sendErrorToParent("Não foi possível obter os dados de autenticação necessários");
        }
    };

    // Função para iniciar o login do Facebook
    const startFacebookLogin = () => {
        // Armazena o token CSRF para validação posterior
        window.csrfToken = csrfToken;

        console.log('Iniciando o login do Facebook com o token CSRF:', csrfToken);

        // Verificar se o token CSRF foi recebido da página principal
        if (!csrfToken || csrfToken.length < 10) {
            sendErrorToParent("Erro de segurança: Token CSRF inválido ou ausente");
            return;
        }

        const configId = configIdParaModo(signupMode);
        if (!configId) {
            sendErrorToParent(
                "Conexão de números já usados no WhatsApp Business não está configurada nesta instalação"
            );
            return;
        }

        // Atualizar o estado para indicar que a autenticação está em andamento
        setStatus('authenticating');

        try {
            window.FB.login(function (response: any) {
                console.log("Resposta do login do Facebook:", response);

                if (response.authResponse) {
                    // Verificar se já recebemos os dados de signup do WhatsApp via postMessage
                    if (signupData.current) {
                        console.log('Usando dados de signup capturados via postMessage:', signupData.current);
                        // Enviar o código e os dados de signup do WhatsApp
                        sendAuthCodeToParent(
                            response.authResponse?.code || null,
                            signupData.current
                        );
                    }
                    // Se temos código de autenticação
                    else if (response.authResponse?.code) {
                        console.log('Enviando código de autenticação:', response.authResponse.code);
                        // Enviar o código para a página pai com o token CSRF
                        sendAuthCodeToParent(response.authResponse.code, response.authResponse);
                    }
                    // Se temos dados de signup direto na resposta
                    else if (response.signupData) {
                        console.log('Usando signupData da resposta:', response.signupData);
                        sendAuthCodeToParent(null, response.signupData);
                    }
                    // Verificar outras possibilidades
                    else {
                        console.log('Verificando outras fontes de dados...');
                        checkWhatsAppSignupData(response);
                    }
                } else {
                    // Usuário cancelou o login ou ocorreu um erro
                    sendErrorToParent("Login cancelado pelo usuário ou ocorreu um erro");
                }
            }, {
                config_id: configId,
                response_type: 'code',
                override_default_response_type: true,
                state: csrfToken, // Adiciona estado para prevenir CSRF
                extras: {
                    setup: {},
                    sessionInfoVersion: '3',
                }
            });
        } catch (error) {
            sendErrorToParent(`Erro ao iniciar login do Facebook: ${sanitizeHTML(String(error))}`);
        }
    };

    // Função para carregar o SDK do Facebook
    const loadFacebookSDK = () => {
        return new Promise<void>((resolve, reject) => {
            // Definir um timeout para detectar falhas de carregamento (como bloqueadores de conteúdo)
            const sdkTimeout = setTimeout(() => {
                reject(new Error("Tempo esgotado ao carregar o SDK do Facebook. O Facebook pode estar bloqueado pelo navegador."));
            }, 10000); // 10 segundos para carregamento

            // Adicionar o SDK do Facebook - esta é a única manipulação necessária do DOM
            const script = document.createElement('script');
            script.src = 'https://connect.facebook.net/pt_BR/sdk.js';
            script.async = true;
            script.defer = true;
            script.onload = () => {
                // Limpar o timeout quando carregado com sucesso
                clearTimeout(sdkTimeout);
                // Inicializar o SDK do Facebook
                window.FB.init({
                    appId: '1089871969306715', // Use o mesmo appId que está configurado no seu aplicativo
                    autoLogAppEvents: true,
                    xfbml: true,
                    version: GRAPH_API_VERSION
                });
                setSdkLoaded(true);
                resolve();
            };
            script.onerror = () => {
                clearTimeout(sdkTimeout);
                // `onerror` entrega um Event, cujo String() é "[object Event]" —
                // inútil para quem lê. Na prática a causa é sempre a mesma:
                // connect.facebook.net está em toda blocklist de rastreadores.
                reject(new Error(
                    "O SDK do Facebook foi bloqueado pelo navegador ou não pôde ser baixado (connect.facebook.net)."
                ));
            };
            document.head.appendChild(script);
        });
    };

    // Função principal para iniciar o login do Facebook
    const initFacebookLogin = () => {
        console.log('Iniciando login do Facebook...');

        // Verificar tentativas de força bruta
        const now = Date.now();
        if (isBlocked) {
            if (now - lastAttemptTime < BLOCK_TIMEOUT) {
                const remainingSecs = Math.ceil((BLOCK_TIMEOUT - (now - lastAttemptTime)) / 1000);
                setRemainingTime(remainingSecs);
                setMessage(`Muitas tentativas. Tente novamente após ${remainingSecs} segundos.`);
                return;
            } else {
                // Reset do bloqueio após timeout
                setIsBlocked(false);
                setAttempts(0);
            }
        }

        // Incrementar contador de tentativas
        setAttempts(prev => {
            const newAttempts = prev + 1;
            if (newAttempts > MAX_ATTEMPTS) {
                setIsBlocked(true);
                setLastAttemptTime(now);
                setStatus('blocked');
                setMessage("Muitas tentativas. Por favor, aguarde antes de tentar novamente.");
                return newAttempts;
            }
            return newAttempts;
        });

        // Verificar se o SDK do Facebook foi carregado
        if (!isSdkLoaded) {
            setStatus('loading');
            setMessage('Carregando SDK do Facebook...');

            loadFacebookSDK()
                .then(() => {
                    console.log('SDK do Facebook carregado com sucesso');
                    startFacebookLogin();
                })
                .catch(error => {
                    console.error('Falha ao carregar SDK do Facebook:', error);

                    // Verificar se é um erro de timeout ou bloqueador
                    if (String(error).includes('bloqueado') || String(error).includes('Tempo esgotado')) {
                        setStatus('error');
                        setMessage(`Não foi possível carregar o SDK do Facebook. O seu navegador pode estar bloqueando conteúdo do Facebook. Desative bloqueadores de conteúdo/popup e tente novamente.`);
                    } else {
                        sendErrorToParent("Falha ao carregar o SDK do Facebook: " + sanitizeHTML(String(error)));
                    }
                });
        } else {
            startFacebookLogin();
        }
    };

    // Verificar origem do opener para segurança
    const validateOpener = (): boolean => {
        if (!window.opener) {
            return false;
        }

        // Validar usando a origin recebida via URL parameter (evita SecurityError cross-origin)
        const allowedOrigins = [
            window.location.origin,
            'https://tera.terachat.com.br',
            'https://app.terachat.com.br',
        ];

        if (openerOrigin && allowedOrigins.some(allowed => openerOrigin.startsWith(allowed) || openerOrigin === allowed)) {
            return true;
        }

        // Se a origin veio da URL, confiar nela (já que o popup foi aberto pelo nosso código)
        if (openerOrigin) {
            return true;
        }

        setStatus('invalid-origin');
        setMessage("Erro de segurança: Não foi possível validar a origem");
        return false;
    };

    // Listener para mensagens do Facebook - captura os dados do Embedded Signup
    const handleFacebookMessage = (event: MessageEvent) => {
        if (!event.origin.endsWith('facebook.com')) return;

        console.log('Recebida mensagem do Facebook:', event.origin, typeof event.data);

        try {
            // Precisamos fazer parse da string para JSON se ainda não for um objeto
            let data;
            if (typeof event.data === 'string') {
                try {
                    data = JSON.parse(event.data);
                } catch (e) {
                    console.error('Erro ao fazer parse da mensagem:', e);
                    return;
                }
            } else {
                data = event.data;
            }

            console.log('Dados parseados do Facebook:', data);

            // Limpamos qualquer dado de signup anterior
            if (data.type === 'WA_EMBEDDED_SIGNUP') {
                // Se o usuário concluiu o fluxo de signup
                if (ehEventoConclusao(data.event)) {
                    console.log('Signup concluído com sucesso:', data.event, data.data);

                    // A coexistência é detectada aqui, na resposta: é o único
                    // ponto em que a Meta diz se o número veio do app WhatsApp
                    // Business (e portanto já está registrado).
                    coexistencia.current = data.event === SIGNUP_FINISH_COEXISTENCIA;

                    // Armazenamos os dados para uso posterior
                    signupData.current = {
                        phone_number_id: data.data.phone_number_id,
                        waba_id: data.data.waba_id,
                        business_id: data.data.business_id
                    };

                    // Exibimos mensagem
                    setMessage('Configuração do WhatsApp concluída. Finalizando registro...');

                    // Se o usuário cancelou o fluxo de signup
                } else if (data.event === 'CANCEL') {
                    const { current_step } = data.data;
                    console.warn("Signup cancelado no passo:", current_step);

                    signupData.current = null;
                    setStatus('error');
                    setMessage('Configuração do WhatsApp cancelada');

                    // Se ocorreu um erro durante o fluxo de signup
                } else if (data.event === 'ERROR') {
                    const { error_message } = data.data;
                    console.error("Erro durante signup:", error_message);

                    signupData.current = null;
                    setStatus('error');
                    setMessage(`Erro na configuração do WhatsApp: ${error_message}`);
                }
            }
        } catch (error) {
            console.log('Erro ao processar mensagem do Facebook:', error, event.data);
        }
    };

    // Efeito para inicialização do componente
    useEffect(() => {
        // Adicionar cabeçalhos de segurança via meta tags
        const addSecurityMetaTags = () => {
            // Configura Content Security Policy
            const csp = document.createElement('meta');
            csp.setAttribute('http-equiv', 'Content-Security-Policy');
            csp.setAttribute('content', "default-src 'self'; connect-src 'self' https://*.facebook.com; script-src 'self' 'unsafe-inline' https://connect.facebook.net; style-src 'self' 'unsafe-inline'; frame-src 'self' https://*.facebook.com");
            document.head.appendChild(csp);

            // Previne MIME sniffing
            const noSniff = document.createElement('meta');
            noSniff.setAttribute('http-equiv', 'X-Content-Type-Options');
            noSniff.setAttribute('content', 'nosniff');
            document.head.appendChild(noSniff);

            // Configuração de referrer policy
            const referrer = document.createElement('meta');
            referrer.setAttribute('name', 'referrer');
            referrer.setAttribute('content', 'strict-origin-when-cross-origin');
            document.head.appendChild(referrer);
        };

        addSecurityMetaTags();

        // Adicionar listener para mensagens do Facebook
        window.addEventListener('message', handleFacebookMessage);

        // Verificar se a janela foi aberta como popup e validar origem
        if (window.opener && validateOpener()) {
            setStatus('ready');
        } else {
            // Se não for um popup ou origem inválida, mostrar mensagem de erro
            setStatus('invalid-origin');
            setMessage('Esta página deve ser acessada através do aplicativo TeraChat.');
        }

        // Detectar quando o usuário fecha o popup antes de concluir a autorização
        const handleUnload = () => {
            if (window.opener && validateOpener()) {
                try {
                    window.opener.postMessage({
                        type: "facebook_auth",
                        error: "Autenticação cancelada",
                        csrfToken: csrfToken // Usar o token do estado React
                    }, openerOrigin);
                } catch (e) {
                    // Ignorar erros ao tentar enviar mensagem
                }
            }
        };

        window.addEventListener('unload', handleUnload);

        return () => {
            window.removeEventListener('message', handleFacebookMessage);
            window.removeEventListener('unload', handleUnload);
            window.csrfToken = '';
        };
    }, []);

    // Efeito para atualizar o contador regressivo quando bloqueado
    useEffect(() => {
        let timer: number;

        if (status === 'blocked' && remainingTime > 0) {
            timer = window.setInterval(() => {
                setRemainingTime(prev => {
                    if (prev <= 1) {
                        setIsBlocked(false);
                        setAttempts(0);
                        setStatus('ready');
                        clearInterval(timer);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }

        return () => {
            if (timer) clearInterval(timer);
        };
    }, [status, remainingTime]);

    // Renderização baseada no status atual
    const renderContent = () => {
        switch (status) {
            case 'loading':
                return (
                    <>
                        <h2 className={styles.title}>Autenticação do Facebook</h2>
                        <p className={styles.text}>{message}</p>
                        <div className={styles.spinner} />
                    </>
                );

            case 'ready':
                return (
                    <>
                        <img src="/logo.png" style={{ height: '40px' }} />
                        <h2 className={styles.title}>Autenticação do Facebook</h2>
                        <p className={styles.text}>Clique no botão abaixo para começar.</p>
                        <p className={styles.text}><b>Não feche esta janela durante o processo de login!</b></p>
                        <p className={styles.text}>Ela será fechada automagicamente após concluir.</p>
                        <button
                            onClick={initFacebookLogin}
                            className={styles.button}
                        >
                            Ok. Iniciar Login!
                        </button>
                    </>
                );

            case 'authenticating':
                return (
                    <>
                        <h2 className={styles.title}>Autenticação em Andamento</h2>
                        <p className={styles.text}>Por favor, siga as instruções na janela do Facebook...</p>
                        <div className={styles.spinner} />
                    </>
                );

            case 'success':
                return (
                    <>
                        <h2 className={styles.title}>Autenticação Concluída!</h2>
                        <p className={styles.text}>{message}</p>
                    </>
                );

            case 'error':
                return (
                    <>
                        <h2 className={styles.title}>Erro na Autenticação</h2>
                        <p className={styles.text}>{message}</p>
                        <div className={styles.buttonGroup}>
                            <button
                                onClick={initFacebookLogin}
                                className={styles.button}
                            >
                                Tentar Novamente
                            </button>
                            <button
                                onClick={() => window.close()}
                                className={styles.buttonSecondary}
                            >
                                Voltar
                            </button>
                        </div>
                        <div className={styles.helpText}>
                            <p>Se o problema persistir:</p>
                            <ul>
                                <li>Verifique se bloqueadores de popup ou de conteúdo estão desativados</li>
                                <li>Tente usar outro navegador</li>
                                <li>Limpe os cookies do Facebook e tente novamente</li>
                            </ul>
                        </div>
                    </>
                );

            case 'blocked':
                return (
                    <>
                        <h2 className={styles.title}>Acesso Temporariamente Bloqueado</h2>
                        <p className={styles.text}>{message}</p>
                        <p className={styles.text}>Tempo restante: {remainingTime} segundos</p>
                    </>
                );

            case 'invalid-origin':
                return (
                    <>
                        <h2 className={styles.title}>Erro de Acesso</h2>
                        <p className={styles.text}>{message}</p>
                        <p className={styles.text}>Esta página deve ser acessada através do aplicativo TeraChat.</p>
                        <button
                            onClick={() => window.location.href = '/'}
                            className={styles.button}
                        >
                            Ir para a página inicial
                        </button>
                    </>
                );

            default:
                return (
                    <>
                        <h2 className={styles.title}>Autenticação do Facebook</h2>
                        <p className={styles.text}>Iniciando processo de autenticação...</p>
                        <div className={styles.spinner} />
                    </>
                );
        }
    };

    return (
        <div className={styles['main-wrapper']}>
            <div className={styles.container}>
                {renderContent()}
            </div>
        </div>
    );
};

export default AuthFbPage;
